import React from 'react';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import type { CampaignStatsResponse } from '@troia-v3/types';

interface CampaignProgressChartProps {
  stats: CampaignStatsResponse;
}

interface SegmentConfig {
  label: string;
  value: number;
  color: string;
}

/**
 * Campaign Progress Chart Component
 * Stacked horizontal bar showing message status breakdown
 */
export const CampaignProgressChart: React.FC<CampaignProgressChartProps> = ({ stats }) => {
  const totalRecipients = stats.totalRecipients || 0;
  const messagesSent = stats.messagesSent || 0;
  const messagesFailed = stats.messagesFailed || 0;
  const messagesDelivered = stats.messagesDelivered || 0;
  const messagesRead = stats.messagesRead || 0;

  const pending = totalRecipients - messagesSent - messagesFailed;
  const deliveredOnly = messagesDelivered - messagesRead;
  const sentOnly = messagesSent - messagesDelivered - messagesFailed;

  const segments: SegmentConfig[] = [
    { label: 'Lidas', value: messagesRead, color: '#8b5cf6' },
    { label: 'Entregues', value: Math.max(0, deliveredOnly), color: '#10b981' },
    { label: 'Enviadas', value: Math.max(0, sentOnly), color: '#3b82f6' },
    { label: 'Falhas', value: messagesFailed, color: '#ef4444' },
    { label: 'Pendentes', value: Math.max(0, pending), color: 'var(--theme-card-border)' },
  ];

  const total = totalRecipients || 1;

  return (
    <Card>
      <CardHeader className="pb-2 py-3 px-4">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>
          Progresso do Envio
        </h3>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {/* Stacked bar */}
        <div
          className="w-full h-6 rounded-full overflow-hidden flex"
          style={{ backgroundColor: 'var(--theme-button-ghost-hover)' }}
        >
          {segments.map((segment) => {
            const widthPercent = (segment.value / total) * 100;
            if (widthPercent <= 0) return null;
            return (
              <div
                key={segment.label}
                className="h-full transition-all duration-500"
                style={{
                  width: `${widthPercent}%`,
                  backgroundColor: segment.color,
                  minWidth: widthPercent > 0 ? '2px' : '0',
                }}
                title={`${segment.label}: ${segment.value} (${widthPercent.toFixed(1)}%)`}
              />
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mt-3">
          {segments.map((segment) => (
            <div key={segment.label} className="flex items-center gap-1.5">
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: segment.color }}
              />
              <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                {segment.label}
              </span>
              <span className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
                {segment.value.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
